// src/templates/modern/menu/components/MenuItemDetailsModal.tsx
// REFACTORED VERSION - Uses extracted hook and utilities
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  FaLeaf,
  FaMinus,
  FaPlus,
  FaSeedling,
  FaShoppingCart,
  FaStar,
  FaTimes,
} from "react-icons/fa";
import { FaWheatAwn } from "react-icons/fa6";
import type { Branch, MenuItem } from "~/types/new_types";
import { getItemName, itemIs, getFormName } from "~/utils/menuItemHelper";
import { VariantTabContent } from "~/components/modal/tabs/VariantTabContent";
import { DetailsTabContent } from "~/components/modal/tabs/DetailsTabContent";
import { IngredientsTabContent } from "~/components/modal/tabs/IngredientsTabContent";
import { NutritionTabContent } from "~/components/modal/tabs/NutritionTabContent";
import { DietaryPreferencesTabContent } from "~/components/modal/tabs/DietaryPreferencesTabContent";
import { useBackButtonInterceptor } from "~/utils/backwordButtonFunctionality";
import { useBodyScrollLock } from "~/hooks/useBodyScrollLock";

// Import the extracted hook
import { useMenuItemDetailsModal } from "~/components/modal/MenuItemDetails/hooks";
import { normalizeTheme } from "~/components/modal/MenuItemDetails/utils";

interface MenuItemDetailsModalProps {
  show: boolean;
  close: () => void;
  theme?:
  | Branch["theme"]
  | {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    cardColor?: string;
    textColor?: string;
    borderColor?: string;
    borderRadius?: string;
    fontFamily?: string;
  };
  item: MenuItem | null;
  isFavorite?: boolean;
  toggleFavorite?: (e: React.MouseEvent) => void;
  /** Callback when feedback button is clicked */
  onFeedback?: (item: MenuItem) => void;
}

interface MenuItemDetailsModalContentProps
  extends Omit<MenuItemDetailsModalProps, "item"> {
  item: MenuItem;
}

const MenuItemDetailsModalContent: React.FC<MenuItemDetailsModalContentProps> = ({
  show,
  close,
  theme,
  item,
  onFeedback,
}) => {
  const { t, i18n } = useTranslation();

  // Use the extracted hook for all state and handlers
  const {
    // Refs
    contentAreaRef,

    // State
    selectedTab,
    setSelectedTab,
    itemCards,
    hasUserInteracted,
    imageHeight,
    isSmallScreen,

    // Derived values
    currentQuantity,
    nonVariantQuantity,
    hasVariants,
    hasVariantsOrCombos,
    shouldShowAddButton,
    displayPrice,
    displayDiscountedPrice,
    nonVariantDisplayQuantity,
    totalCardQuantity,
    isRTL,
    totalPrice,

    // Context values
    isCartDisabled,
    branch,
    getConvertedPrice,

    // Handlers
    handleClose,
    handleFirstAdd,
    handleQuantityDecrease,
    handleQuantityIncrease,
    handleSave,
    addCard,
    removeCard,
    updateCardVariant,
    updateCardQuantity,
  } = useMenuItemDetailsModal({ item, show, close });

  // Normalize theme
  const safeTheme = normalizeTheme(theme);

  // Back button interceptor
  useBackButtonInterceptor(show, handleClose);

  // useBodyScrollLock(show);

  // Calculate modal colors
  const modalBackgroundColor = safeTheme.cardColor;
  const modalTextColor = safeTheme.textColor;

  // Calculate display price with converted values
  const convertedTotalPrice = getConvertedPrice(totalPrice);

  // Tab configuration
  const tabs = [
    {
      id: "variant" as const,
      label: t("menuItem.customize"),
      show: hasVariantsOrCombos,
    },
    {
      id: "details" as const,
      label: t("menuItem.details"),
      show: true,
    },
    {
      id: "ingredients" as const,
      label: t("menuItem.ingredients"),
      show: Boolean(item.ingredients && item.ingredients.length > 0),
    },
    {
      id: "nutrition" as const,
      label: t("menuItem.nutrition"),
      show: Boolean(item.calories || item.preparation_time),
    },
    {
      id: "dietary" as const,
      label: t("menuItem.dietary"),
      show: Boolean(item.dietary_preferences && item.dietary_preferences.length > 0),
    },
  ].filter((tab) => tab.show);

  return (
    <AnimatePresence mode="sync">
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.1 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={handleClose}
          style={{ direction: isRTL ? "rtl" : "ltr" }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            className={`w-full flex flex-col overflow-hidden shadow-2xl ${isSmallScreen
              ? "h-full max-w-full"
              : "max-w-2xl h-[90vh] md:h-[85vh] rounded-2xl"
              }`}
            style={{
              backgroundColor: modalBackgroundColor,
              color: modalTextColor,
              borderRadius: isSmallScreen ? 0 : safeTheme.borderRadius,
            }}
          >
            {/* ========== MODAL HEADER ========== */}
            <header className="relative">
              {/* Image container with dynamic height */}
              <div
                className="relative overflow-hidden transition-[height] duration-150 ease-out"
                style={{ height: imageHeight }}
              >
                {/* Background image */}
                <img
                  src={
                    item.image_url ??
                    branch?.default_item_image ??
                    "/images/no-image-available.jpg"
                  }
                  alt={item.name}
                  className="w-full h-full object-cover pointer-events-none select-none"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/images/broken-image.jpg";
                  }}
                />

                {/* Dark gradient overlay */}
                <div
                  className="absolute inset-0"
                  style={{
                    background: "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.8))",
                  }}
                />

                {/* ===== TOP ROW: Badges (left) + Action Buttons (right) ===== */}
                <div className="absolute top-3 sm:top-4 inset-x-3 sm:inset-x-4 flex items-start justify-between">
                  {/* Left: Dietary & Featured badges */}
                  <div 
                    className="flex gap-1.5 sm:gap-2 flex-wrap max-w-[60%] transition-opacity duration-150"
                    style={{ opacity: imageHeight < 100 ? 0 : 1, pointerEvents: imageHeight < 100 ? 'none' : 'auto' }}
                  >
                    {item.is_featured && (
                      <div
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-50 text-yellow-700 text-[10px] sm:text-xs font-semibold flex items-center gap-1"
                        style={{ borderRadius: safeTheme.borderRadius }}
                      >
                        <FaStar className="text-yellow-500" />
                        <span>{t("menu.popular")}</span>
                      </div>
                    )}
                    {itemIs(item, "vegetarian") && (
                      <div
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-50 text-green-700 text-[10px] sm:text-xs font-semibold flex items-center gap-1"
                        style={{ borderRadius: safeTheme.borderRadius }}
                      >
                        <FaLeaf />
                        <span>{t("menu.vegetarian")}</span>
                      </div>
                    )}
                    {itemIs(item, "vegan") && (
                      <div
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-100 text-green-800 text-[10px] sm:text-xs font-semibold flex items-center gap-1"
                        style={{ borderRadius: safeTheme.borderRadius }}
                      >
                        <FaSeedling />
                        <span>{t("menu.vegan")}</span>
                      </div>
                    )}
                    {itemIs(item, "gluten-free") && (
                      <div
                        className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-amber-50 text-amber-700 text-[10px] sm:text-xs font-semibold flex items-center gap-1"
                        style={{ borderRadius: safeTheme.borderRadius }}
                      >
                        <FaWheatAwn />
                        <span>{t("menu.glutenFree")}</span>
                      </div>
                    )}
                  </div>

                  {/* Right: Action buttons (Feedback + Close) */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {onFeedback && item.is_available && (
                      <button
                        onClick={() => onFeedback(item)}
                        className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center backdrop-blur-sm cursor-pointer active:scale-95 transition-transform"
                        style={{ backgroundColor: "rgba(0,0,0,0.6)", color: "white" }}
                        title={t("feedback.giveFeedback")}
                      >
                        <FaStar size={14} />
                      </button>
                    )}
                    <button
                      onClick={handleClose}
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center backdrop-blur-sm cursor-pointer active:scale-95 transition-transform z-30"
                      style={{ backgroundColor: "rgba(0,0,0,0.6)", color: "white" }}
                    >
                      <FaTimes />
                    </button>
                  </div>
                </div>

                {/* ===== BOTTOM ROW: Item name + Price ===== */}
                <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6 text-white">
                  <div className="flex items-center justify-between gap-3">
                    {/* Item name */}
                    <h2 className={`font-bold flex-1 min-w-0 ${imageHeight < 100 ? 'text-base truncate' : 'text-lg sm:text-2xl line-clamp-2'}`}>
                      {getItemName(item, i18n.language)}
                    </h2>
                    
                    {/* Price */}
                    <div className="flex-shrink-0">
                      {item.discounted_price ? (
                        <div className={`flex items-center gap-2 ${imageHeight < 100 ? '' : 'sm:flex-col sm:items-end sm:gap-0'}`}>
                          <span className={`line-through opacity-70 ${imageHeight < 100 ? 'text-[10px]' : 'text-xs sm:text-sm'}`}>
                            {displayPrice}
                          </span>
                          <span className={`font-bold ${imageHeight < 100 ? 'text-sm' : 'text-base sm:text-xl'}`}>
                            {displayDiscountedPrice}
                          </span>
                        </div>
                      ) : (
                        <div className={`font-bold ${imageHeight < 100 ? 'text-sm' : 'text-base sm:text-xl'}`}>
                          {displayPrice}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Unavailable overlay (covers entire image) */}
                {!item.is_available && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center pointer-events-auto select-none z-20">
                    <img
                      src={`/images/not-availbale-stampel-${i18n.language}.png`}
                      className="h-2/3 -rotate-10 pointer-events-none select-none"
                    />
                  </div>
                )}
              </div>
            </header>

            {/* Tab navigation */}
            {tabs.length > 1 && (
              <div
                className="flex overflow-x-auto scrollbar-hide border-b"
                style={{
                  borderColor: safeTheme.borderColor,
                  backgroundColor: safeTheme.cardColor,
                }}
              >
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors relative ${selectedTab === tab.id ? "text-primary" : "text-gray-500"
                      }`}
                    style={{
                      color:
                        selectedTab === tab.id
                          ? safeTheme.primaryColor
                          : `${safeTheme.textColor}80`,
                    }}
                  >
                    {tab.label}
                    {selectedTab === tab.id && (
                      <div
                        className="absolute bottom-0 left-0 right-0 h-0.5"
                        style={{ backgroundColor: safeTheme.primaryColor }}
                      />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Scrollable content area */}
            <div
              ref={contentAreaRef}
              className="flex-1 overflow-y-auto"
              style={{ backgroundColor: safeTheme.backgroundColor }}
            >
              <div className="p-4">
                {selectedTab === "variant" && (
                  <VariantTabContent
                    item={item}
                    theme={safeTheme}
                    language={i18n.language}
                    itemCards={itemCards}
                    currentQuantity={currentQuantity}
                    updateCardVariant={updateCardVariant}
                    updateCardQuantity={updateCardQuantity}
                    onAddItemCard={addCard}
                    onRemoveItemCard={removeCard}
                  />
                )}
                {selectedTab === "details" && (
                  <DetailsTabContent
                    item={item}
                    theme={safeTheme}
                    language={i18n.language}
                  />
                )}
                {selectedTab === "ingredients" && (
                  <IngredientsTabContent
                    item={item}
                    theme={safeTheme}
                    language={i18n.language}
                  />
                )}
                {selectedTab === "nutrition" && (
                  <NutritionTabContent
                    item={item}
                    theme={safeTheme}
                    language={i18n.language}
                  />
                )}
                {selectedTab === "dietary" && (
                  <DietaryPreferencesTabContent
                    item={item}
                    language={i18n.language}
                  />
                )}
              </div>
            </div>

            {/* Footer with quantity controls and add button */}
            {item.is_available && !isCartDisabled && (
              <div
                className="sticky bottom-0 p-4 border-t"
                style={{
                  backgroundColor: safeTheme.cardColor,
                  borderColor: safeTheme.borderColor,
                }}
              >
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  {/* Add button (shown when item not yet added) */}
                  {shouldShowAddButton && (
                    <button
                      onClick={handleFirstAdd}
                      className="flex-1 min-w-[200px] py-3 px-6 rounded-xl text-white font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                      style={{ backgroundColor: safeTheme.primaryColor }}
                    >
                      <FaShoppingCart />
                      <span>{t("menu.addToOrder")}</span>
                    </button>
                  )}

                  {/* Quantity controls (shown after adding) */}
                  {!shouldShowAddButton && (
                    <>
                      {/* Quantity controls group */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Minus button */}
                        <button
                          onClick={handleQuantityDecrease}
                          className="w-10 h-10 rounded-full flex items-center justify-center border-2 active:scale-95 transition-transform"
                          style={{
                            borderColor: safeTheme.borderColor,
                            backgroundColor: safeTheme.cardColor,
                          }}
                        >
                          <FaMinus size={12} style={{ color: safeTheme.textColor }} />
                        </button>

                        {/* Quantity display */}
                        <div
                          className="min-w-[32px] text-center font-bold text-lg"
                          style={{ color: safeTheme.textColor }}
                        >
                          {hasVariants ? totalCardQuantity : nonVariantDisplayQuantity}
                        </div>

                        {/* Plus button */}
                        <button
                          onClick={handleQuantityIncrease}
                          className="w-10 h-10 rounded-full flex items-center justify-center active:scale-95 transition-transform"
                          style={{ backgroundColor: safeTheme.primaryColor }}
                        >
                          <FaPlus size={12} style={{ color: "#fff" }} />
                        </button>
                      </div>

                      {/* Price + Save button group (wrap together) */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Price Display */}
                        {totalPrice > 0 && (
                          <span
                            className="text-lg font-bold px-2"
                            style={{ color: safeTheme.primaryColor }}
                          >
                            {typeof convertedTotalPrice === "number"
                              ? `$${(convertedTotalPrice as number).toFixed(2)}`
                              : convertedTotalPrice}
                          </span>
                        )}

                        {/* Save/Update button */}
                        <button
                          onClick={handleSave}
                          className="py-2.5 px-5 rounded-full text-white font-semibold flex items-center justify-center shadow-lg active:scale-[0.98] transition-transform whitespace-nowrap text-sm"
                          style={{ backgroundColor: safeTheme.primaryColor }}
                        >
                          <span>
                            {currentQuantity > 0 ? t("menu.update") : t("menu.saveToOrder")}
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Unavailable message footer */}
            {!item.is_available && (
              <div
                className="p-4 border-t text-center"
                style={{
                  backgroundColor: safeTheme.cardColor,
                  borderColor: safeTheme.borderColor,
                }}
              >
                <span
                  className="text-sm font-medium opacity-60"
                  style={{ color: safeTheme.textColor }}
                >
                  {t("menu.unavailable")}
                </span>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const MenuItemDetailsModal: React.FC<MenuItemDetailsModalProps> = ({
  item,
  ...rest
}) => {
  if (!item) return null;
  return <MenuItemDetailsModalContent {...rest} item={item} />;
};

export default MenuItemDetailsModal;
